import React, { useMemo, useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { ShieldCheck, Lock, Users, CheckSquare, Settings, Key, Shield, Award, AlertCircle } from 'lucide-react';
import { rolesAPI } from '../services/api';

const ManagementAuthorization = () => {
  const { t } = useTranslation();
  const [roles, setRoles] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [rolePermissions, setRolePermissions] = useState({});
  const [selectedRole, setSelectedRole] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const rolePermissionsRef = useRef({});
  const saveQueueRef = useRef(Promise.resolve());

  const selectedRolePerms = useMemo(() => 
    selectedRole ? new Set(rolePermissions[selectedRole.id] || []) : new Set(), 
    [rolePermissions, selectedRole]
  );

  // Load data from API
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        const [rolesRes, permissionsRes] = await Promise.all([
          rolesAPI.list(),
          rolesAPI.getPermissions()
        ]);
        
        setRoles(rolesRes.data || []);
        setPermissions(permissionsRes.data || []);
        
        // Load permissions for each role
        const rolePermsMap = {};
        for (const role of rolesRes.data || []) {
          const rolePermsRes = await rolesAPI.getRolePermissions(role.id);
          rolePermsMap[role.id] = rolePermsRes.data || [];
        }
        setRolePermissions(rolePermsMap);
        rolePermissionsRef.current = rolePermsMap;
        
        // Set default selected role
        if (rolesRes.data?.length > 0) {
          setSelectedRole(rolesRes.data[0]);
        }
        
      } catch (e) {
        setError(e.message || t('managementAuthorization.loadError'));
      } finally {
        setLoading(false);
      }
    };
    
    loadData();
  }, []);

  useEffect(() => {
    rolePermissionsRef.current = rolePermissions;
  }, [rolePermissions]);

  const togglePerm = (permissionKey) => {
    if (!selectedRole) return;

    const roleId = selectedRole.id;
    const currentPerms = rolePermissionsRef.current[roleId] || [];
    const hasPermission = currentPerms.includes(permissionKey);
    const updatedPerms = hasPermission
      ? currentPerms.filter((p) => p !== permissionKey)
      : [...currentPerms, permissionKey];
    const previousPerms = currentPerms;

    const nextRolePermissions = {
      ...rolePermissionsRef.current,
      [roleId]: updatedPerms,
    };
    rolePermissionsRef.current = nextRolePermissions;
    setRolePermissions(nextRolePermissions);

    saveQueueRef.current = saveQueueRef.current
      .catch(() => {})
      .then(async () => {
        const permsToSave = rolePermissionsRef.current[roleId] || [];
        setSaving(true);
        try {
          await rolesAPI.updateRolePermissions(roleId, permsToSave);
        } catch (e) {
          try {
            const rolePermsRes = await rolesAPI.getRolePermissions(roleId);
            const serverPerms = rolePermsRes.data || [];
            rolePermissionsRef.current = {
              ...rolePermissionsRef.current,
              [roleId]: serverPerms,
            };
            setRolePermissions((prev) => ({
              ...prev,
              [roleId]: serverPerms,
            }));
          } catch {
            rolePermissionsRef.current = {
              ...rolePermissionsRef.current,
              [roleId]: previousPerms,
            };
            setRolePermissions((prev) => ({
              ...prev,
              [roleId]: previousPerms,
            }));
          }
          setError(e.message || t('managementAuthorization.updateError'));
        } finally {
          setSaving(false);
        }
      });
  };

  const getRoleIcon = (role) => {
    switch(role) {
      case 'admin': return <Shield className="h-5 w-5" />;
      case 'operator': return <Users className="h-5 w-5" />;
      case 'viewer': return <Award className="h-5 w-5" />;
      default: return <Users className="h-5 w-5" />;
    }
  };

  const getRoleColor = (role) => {
    switch(role) {
      case 'admin': return 'bg-red-500';
      case 'operator': return 'bg-blue-500';
      case 'viewer': return 'bg-green-500';
      default: return 'bg-gray-500';
    }
  };

  return (
    <main className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8 flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 flex items-center">
              <ShieldCheck className="h-8 w-8 mr-3 text-blue-600" />
              {t('managementAuthorization.title')}
            </h1>
            <p className="text-gray-600 mt-2">{t('managementAuthorization.subtitle')}</p>
          </div>
          <div className="hidden md:flex items-center space-x-4">
            <div className="text-center">
              <div className="text-2xl font-bold text-blue-600">{roles.length}</div>
              <div className="text-sm text-gray-500">{t('managementAuthorization.roles')}</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-purple-600">{permissions.length}</div>
              <div className="text-sm text-gray-500">{t('managementAuthorization.permissions')}</div>
            </div>
          </div>
        </div>

        {/* Error Alert */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl flex items-center space-x-3">
            <AlertCircle className="h-5 w-5 text-red-600" />
            <span className="text-red-700 font-medium">{error}</span>
            <button onClick={() => setError(null)} className="ml-auto text-red-600 hover:text-red-800">
              <span className="sr-only">{t('managementAuthorization.dismiss')}</span>
              ×
            </button>
          </div>
        )}

        {/* Loading State */}
        {loading ? (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <p className="text-gray-500 mt-2">{t('managementAuthorization.loading')}</p>
          </div>
        ) : (
          <>

        {/* Role Cards Overview */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          {roles.map(role => {
            const permCount = (rolePermissions[role.id] || []).length;
            const isSelected = selectedRole?.id === role.id;
            return (
              <div
                key={role.id}
                onClick={() => setSelectedRole(role)}
                className={`p-6 rounded-xl border-2 cursor-pointer transition-all duration-200 transform hover:scale-105 ${
                  isSelected 
                    ? 'border-blue-500 bg-blue-50 shadow-lg' 
                    : 'border-gray-200 bg-white hover:border-gray-300 shadow-sm'
                }`}
              >
                <div className="flex items-center justify-between mb-4">
                  <div className={`p-2 rounded-lg text-white ${getRoleColor(role.name)}`}>
                    {getRoleIcon(role.name)}
                  </div>
                  {isSelected && <div className="w-3 h-3 bg-blue-500 rounded-full animate-pulse"></div>}
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">{role.display_name}</h3>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-600">{t('managementAuthorization.permissionsCount', { value: permCount })}</span>
                  <span className={`px-2 py-1 rounded-full text-xs ${isSelected ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600'}`}>
                    {t('managementAuthorization.accessPercent', { value: permissions.length > 0 ? Math.round((permCount / permissions.length) * 100) : 0 })}
                  </span>
                </div>
              </div>
            );
          })}
        </div>

        {/* Permissions Configuration */}
        <div className="bg-white rounded-xl shadow-lg border border-gray-200">
          <div className="p-6 border-b border-gray-100">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <div className={`p-2 rounded-lg text-white mr-3 ${getRoleColor(selectedRole?.name || '')}`}>
                  {getRoleIcon(selectedRole?.name || '')}
                </div>
                <div>
                  <h2 className="text-xl font-semibold text-gray-900">
                    {t('managementAuthorization.rolePermissionsTitle', { role: selectedRole?.display_name || t('managementAuthorization.selectRole') })}
                  </h2>
                  <p className="text-gray-500 text-sm">{t('managementAuthorization.configureAccess')}</p>
                </div>
              </div>
              <div className="flex items-center space-x-2 text-sm text-gray-500">
                <Key className="h-4 w-4" />
                <span>{t('managementAuthorization.enabledCount', { enabled: selectedRolePerms.size, total: permissions.length })}</span>
                {saving && <span className="text-blue-600">{t('common.saving')}</span>}
              </div>
            </div>
          </div>
          
          <div className="p-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {permissions.map(permission => {
                const isEnabled = selectedRolePerms.has(permission.key_name);
                return (
                  <label 
                    key={permission.id} 
                    className={`group flex items-center p-4 border-2 rounded-lg cursor-pointer transition-all duration-200 hover:shadow-md ${
                      isEnabled 
                        ? 'border-green-300 bg-green-50 hover:border-green-400' 
                        : 'border-gray-200 bg-gray-50 hover:border-gray-300'
                    } ${saving ? 'opacity-50 pointer-events-none' : ''}`}
                  >
                    <div className="relative">
                      <input
                        type="checkbox"
                        checked={isEnabled}
                        onChange={() => togglePerm(permission.key_name)}
                        className="sr-only"
                        disabled={saving}
                      />
                      <div className={`w-5 h-5 rounded-md border-2 flex items-center justify-center transition-all duration-200 ${
                        isEnabled 
                          ? 'bg-green-500 border-green-500' 
                          : 'border-gray-300 group-hover:border-gray-400'
                      }`}>
                        {isEnabled && <CheckSquare className="h-3 w-3 text-white" />}
                      </div>
                    </div>
                    <span className={`ml-3 text-sm font-medium ${
                      isEnabled ? 'text-green-800' : 'text-gray-700'
                    }`}>
                      {permission.display_name}
                    </span>
                  </label>
                );
              })}
            </div>
          </div>
        </div>

        {/* Role Information Cards */}
        <div className="mt-8 grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <Settings className="h-5 w-5 text-gray-600 mr-2" />
              {t('managementAuthorization.roleHierarchy')}
            </h3>
            <div className="space-y-3">
              {[
                { role: t('managementAuthorization.hierAdmin'), desc: t('managementAuthorization.hierAdminDesc'), color: 'text-red-600', bg: 'bg-red-50' },
                { role: t('managementAuthorization.hierOperation'), desc: t('managementAuthorization.hierOperationDesc'), color: 'text-blue-600', bg: 'bg-blue-50' },
                { role: t('managementAuthorization.hierViewer'), desc: t('managementAuthorization.hierViewerDesc'), color: 'text-green-600', bg: 'bg-green-50' }
              ].map(item => (
                <div key={item.role} className={`p-3 rounded-lg ${item.bg}`}>
                  <div className={`font-medium ${item.color}`}>{item.role}</div>
                  <div className="text-sm text-gray-600 mt-1">{item.desc}</div>
                </div>
              ))}
            </div>
          </div>
          
          <div className="bg-gradient-to-br from-amber-50 to-orange-100 rounded-xl shadow-lg p-6 border border-amber-200">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <Lock className="h-5 w-5 text-amber-600 mr-2" />
              {t('managementAuthorization.securityNotice')}
            </h3>
            <div className="space-y-3 text-sm">
              <div className="flex items-start">
                <div className="w-2 h-2 bg-amber-500 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                <div className="text-gray-700">{t('managementAuthorization.notice1')}</div>
              </div>
              <div className="flex items-start">
                <div className="w-2 h-2 bg-amber-500 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                <div className="text-gray-700">{t('managementAuthorization.notice2')}</div>
              </div>
            </div>
          </div>
        </div>
          </>
        )}
      </div>
    </main>
  );
};

export default ManagementAuthorization;
